import {
  DeviceIsDeviceType,
  DeviceIsSystemType,
} from 'features/Integrations/utils/deviceComparison.utils';

/**
 * The stale rule (Mike, 2026-09-04).
 *
 * The page load receives ONE newest row for each device, whatever its age
 * (`GET /v2/integrations/device/data/latest`). A gauge that stopped reporting
 * a month ago would otherwise show its last status as if it were current. So:
 * when the displayed row is older than the threshold, the status is Offline.
 *
 * The rule applies ONLY to the telemetry types that report on a schedule —
 * Stage (Flood), Monitoring Station (USGS) and Buoy Conditions. Sirens,
 * flashing lights and manual road closures are event-only: they write a row
 * only when a person commands them, so an idle siren's last row is correctly
 * weeks old and the siren is healthy. Overwatch VMS / Exacq Vision carry no
 * data rows at all. Those types are exempt.
 *
 * The backend owns the threshold (`DEVICE_DATA_STALE_MS`). It arrives as
 * `meta.staleAfterMs` on the latest-row response and `devices.api.js` stores
 * it here with `setDeviceDataStaleAfterMs`. Until it arrives: one hour.
 */
export const DEFAULT_DEVICE_DATA_STALE_AFTER_MS = 60 * 60 * 1000;

let staleAfterMs = DEFAULT_DEVICE_DATA_STALE_AFTER_MS;

/** Stores the backend-owned threshold. A non-finite or non-positive value is ignored. */
export function setDeviceDataStaleAfterMs(ms) {
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms <= 0) return;
  staleAfterMs = ms;
}

export function getDeviceDataStaleAfterMs() {
  return staleAfterMs;
}

/**
 * True ONLY for the telemetry types that report on a schedule. Every other
 * type — the event-only RescAlert devices and the cameras — is exempt, and so
 * is any type this file does not know: an unknown type must never read
 * Offline because of a rule written for gauges.
 */
export function deviceUsesStaleRule(device) {
  if (!device) return false;
  return (
    DeviceIsSystemType(device, 'Flood') ||
    DeviceIsDeviceType(device, 'Stage') ||
    DeviceIsSystemType(device, 'USGS') ||
    DeviceIsDeviceType(device, 'Monitoring Station') ||
    DeviceIsDeviceType(device, 'Buoy Conditions')
  );
}

/**
 * True when the rule applies to the device and the point is older than the
 * threshold. A point without a finite `ts` is NOT stale: no timestamp is no
 * information, and the status functions already treat "no data" on their own.
 */
export function isDeviceDataStale(device, point, now = Date.now()) {
  if (!deviceUsesStaleRule(device)) return false;
  const ts = point?.ts;
  if (!Number.isFinite(ts)) return false;
  return now - ts > staleAfterMs;
}
